import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatISODay, formatMinutes, weekdayRu, type TimeFormat } from '../../lib/time';
import { plural, pluralWithCount } from '../../lib/plural';
import { Avatar, Notice, Pill } from '../../ui/parts';
import { useStaffChild, useStaffChildren, useStopSeries } from '../api';
import type { Series, StaffChild } from '../types';

/** Вкладка «Мои дети»: кто закреплён и на каком расписании занимается. */
export function Kids() {
  const children = useStaffChildren();
  const tf = useMeContext().center.timeFormat;
  const sheet = useSheet();
  const list = children.data?.pages.flatMap((p) => p.items) ?? [];
  const total = children.data?.pages[0]?.total ?? 0;

  useScreen('Мои дети', null);

  return (
    <Screen>
      <div className="sechead">
        <p className="eyebrow">Закреплены за мной</p>
        <span className="lead" style={{ fontSize: 13 }}>
          {total ? pluralWithCount(total, 'ребёнок', 'ребёнка', 'детей') : ''}
        </span>
      </div>

      {children.isPending ? <p className="lead">Загружаем…</p> : null}

      <div className="rows">
        {list.map((c) => (
          <button className="rw" key={c.id} onClick={() => sheet.open(<ChildSheet child={c} />)}>
            <Avatar name={c.name} small />
            <span className="rw-body">
              <b>{c.name}{c.birth_year ? `, ${age(c.birth_year)}` : ''}</b>
              <span>{c.series.length ? c.series.map((x) => seriesLine(x, tf)).join(' · ') : 'разовые записи'}</span>
            </span>
            {c.series.length ? <Pill kind="paid">серия</Pill> : null}
          </button>
        ))}
      </div>

      {children.hasNextPage ? (
        <button className="add" disabled={children.isFetchingNextPage} onClick={() => void children.fetchNextPage()}>
          {children.isFetchingNextPage ? 'Загружаем…' : `Показать ещё · осталось ${total - list.length}`}
        </button>
      ) : null}

      {!children.isPending && list.length === 0 ? <p className="lead">За вами пока никого не закрепили.</p> : null}

      <Notice kind="calm">
        Регулярные записи задаёте вы: администратор их видит, но не меняет.
      </Notice>
    </Screen>
  );
}

function ChildSheet({ child }: { child: StaffChild }) {
  const me = useMeContext();
  const writable = useWritable();
  const navigate = useNavigate();
  const sheet = useSheet();
  const toast = useToast();
  const card = useStaffChild(child.id);
  const stop = useStopSeries();

  const mine = child.series;
  const others = (card.data?.team ?? []).filter((t) => t.specialist_id !== me.specialistId);

  return (
    <>
      <h3>{child.name}{child.birth_year ? `, ${age(child.birth_year)}` : ''}</h3>
      <p>
        Родитель: {child.parent_name} · {child.phone}
        {child.tg_id ? '' : ' · '}
        {child.tg_id ? null : <b>бота не открывал, напоминания не приходят</b>}
      </p>

      <p className="eyebrow">Регулярные записи</p>
      {mine.length ? (
        <div className="rows">
          {mine.map((s) => (
            <div className="rw" key={s.id}>
              <span className="rw-body">
                <b>{seriesLine(s, me.center.timeFormat)}</b>
                <span>
                  {s.service_title}
                  {s.ends_on ? ` · до ${formatISODay(s.ends_on, { weekday: false })}` : ''}
                  {' · '}
                  {pluralWithCount(s.made, 'запись', 'записи', 'записей')}
                </span>
              </span>
              {writable ? (
                <button
                  className="btn ghost-danger"
                  style={{ flex: '0 0 auto', padding: '8px 12px' }}
                  disabled={stop.isPending}
                  onClick={() =>
                    stop.mutate(s.id, {
                      onSuccess: () => toast('Серия остановлена, будущие записи сняты'),
                      onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось остановить'),
                    })
                  }
                >
                  Стоп
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="lead">Пока нет — записи создаются по одной.</p>
      )}

      {others.length ? (
        <>
          <p className="eyebrow" style={{ marginTop: 2 }}>Ребёнка также ведут</p>
          <div className="rows">
            {others.map((t) => (
              <div className="rw" key={t.specialist_id}>
                <Avatar name={t.specialist_name} photo={t.specialist_photo} small />
                <span className="rw-body">
                  <b>{t.specialist_name}</b>
                  <span>{t.direction_title}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {writable ? (
        <div className="row-actions">
          <button
            className="btn solid"
            onClick={() => {
              sheet.close();
              navigate(`/staff/kids/${child.id}/series`);
            }}
          >
            {mine.length ? 'Новая серия' : 'Задать расписание'}
          </button>
        </div>
      ) : null}
      <button className="btn" onClick={() => sheet.close()}>Закрыть</button>
    </>
  );
}

export function seriesLine(s: Series, tf: TimeFormat): string {
  const days = parseWeekdays(s.weekdays).map((d) => weekdayRu(d)).join(', ');
  return `${days} в ${formatMinutes(s.time_min, tf)}`;
}

/** Дни недели хранятся строкой: «1,3». */
export function parseWeekdays(raw: string): number[] {
  return String(raw ?? '')
    .split(',')
    .map((x) => Number(x.trim()))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
}

function age(birthYear: number): string {
  const n = new Date().getUTCFullYear() - birthYear;
  return `${n} ${plural(n, 'год', 'года', 'лет')}`;
}
